import { Component, computed, DestroyRef, inject, input, signal, Signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { A11yModule } from '@angular/cdk/a11y';
import { MatButtonModule } from '@angular/material/button';
import { Router, RouterLink } from '@angular/router';
import { Project } from '../models';
import { ProjectDomainService } from '../domain-services/project-domain.service';
import { catchError, EMPTY, finalize, of } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Derived from the API's ProjectAggregate.TryChangeName pattern:
//   ^([\w\d\-\s\p{Lu}\p{Lt}\p{Ll}\p{Lo}\p{Lm}]+)$
// In .NET, \w is [\p{L}\p{Mn}\p{Nd}\p{Pc}] and the trailing letter categories are
// already covered by \p{L}, so the API accepts letters of any script, combining
// marks, digits, connector punctuation, hyphen, and whitespace. Common
// punctuation is rejected — including . , ' " & ( ) / : ; ! ? # @ + and the
// typographic en/em dashes, which are not the ASCII hyphen.
//
// We deliberately diverge on one point: \p{Pc} (underscore and its connector
// relatives) is dropped, so underscores are not allowed in project names. That
// is stricter than the API, which is the safe direction — the client never lets
// through a name the server would reject with a bare 422. The consequence is
// that a project already named with an underscore must have it removed before
// the project can be renamed.
const PROJECT_NAME_PATTERN = /^[\p{L}\p{Mn}\p{Nd}\-\s]+$/u;

const RESERVED_PROJECT_NAME = 'configure';

@Component({
  selector: 'app-new-project-dialog',
  imports: [MatFormFieldModule, MatInputModule, A11yModule, MatButtonModule, RouterLink],
  templateUrl: './new-project-dialog.html',
  styleUrl: './new-project-dialog.scss',
})
export class NewProjectDialog {
  readonly existingProjects = input<Project[]>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly projectDomain = inject(ProjectDomainService);
  private readonly router = inject(Router);

  constructor() {
    const current = this.projectDomain.currentProject();
    if (current?.id) {
      this.projectName.set(current.name);
    }

    if (!this.projectDomain.projects()) {
      this.projectDomain
        .loadProjects()
        .pipe(catchError(() => of([])))
        .subscribe();
    }
  }

  projectName = signal('');
  isLoading = signal(false);
  apiError = signal<string | null>(null);

  readonly isUpdateOperation = computed(() => !!this.projectDomain.currentProject());
  readonly existingProjectsView = computed<Project[]>(() => {
    return this.projectDomain.projects() ?? this.existingProjects() ?? [];
  });

  // Ordered to match the API's TryChangeName else-if chain, which stops at the
  // first failure. Applied to create as well as rename: the API only validates
  // on PUT, so a name accepted at creation but rejected by TryChangeName would
  // leave the project permanently unrenameable — and, because PUT revalidates
  // the name on every update, unable to change its notification emails either.
  readonly validationErrors = computed<string[]>(() => {
    const name = this.projectName();
    const current = this.projectDomain.currentProject();
    const validationErrors: string[] = [];

    if (name.trim().length === 0) {
      validationErrors.push('Project name is required');
    } else if (name.length > 100) {
      validationErrors.push('Project name cannot be more than 100 characters');
    } else if (!PROJECT_NAME_PATTERN.test(name)) {
      validationErrors.push(
        'Project name can only contain letters, numbers, spaces, and dashes',
      );
    } else if (
      this.existingProjectsView().some(
        (project) => project.id !== current?.id && project.name.toLowerCase() === name.toLowerCase(),
      )
    ) {
      validationErrors.push('Project name already exists');
    } else if (name.toLowerCase() === RESERVED_PROJECT_NAME) {
      validationErrors.push('Project name is reserved');
    } else if (current?.name.toLowerCase() === RESERVED_PROJECT_NAME) {
      validationErrors.push('Cannot rename reserved project');
    }

    return validationErrors;
  });

  // Shown to the user. A server error is displayed but does not disable the
  // button: unlike a validation error it may be transient, and the user needs
  // to be able to retry without first editing the name.
  errors = computed<string[]>(() => {
    const all = [...this.validationErrors()];
    const apiErr = this.apiError();
    if (apiErr) all.push(apiErr);
    return all;
  });

  onNameInput(e: Event): void {
    const el = e.target as HTMLInputElement | null;
    if (!el) return;
    this.projectName.set(el.value);
    this.apiError.set(null);
  }

  onReadyClick(): void {
    if (this.validationErrors().length > 0 || this.isLoading()) return;

    this.isLoading.set(true);
    this.apiError.set(null);

    const existingProject = this.projectDomain.currentProject();
    const isUpdateOperation = !!existingProject;

    const operation$ = isUpdateOperation
      ? this.projectDomain.updateProject({ ...existingProject, name: this.projectName() })
      : this.projectDomain.createProject(this.projectName());

    operation$
      .pipe(
        catchError((error) => {
          this.apiError.set(this.describeSaveError(error));

          // The API answers 422 with an empty body, so the most useful thing we
          // can do is re-read the project list: if the cause was a name taken
          // since this dialog opened, the client-side uniqueness rule will
          // catch it on the next attempt and say so precisely.
          if (error instanceof HttpErrorResponse && error.status === 422) {
            this.projectDomain
              .loadProjects()
              .pipe(catchError(() => of([])))
              .subscribe();
          }
          return EMPTY;
        }),
        finalize(() => {
          this.isLoading.set(false);
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (result) => {
          console.log(`Project ${isUpdateOperation ? 'updated' : 'created'} successfully:`, result);
          this.navigateAfterSuccess(result, isUpdateOperation);
        },
      });
  }

  // The API returns bare status codes with no body, so the status is all we
  // have to work from. Anything else is an error thrown by ApiCreateService,
  // whose own message is more specific than a substitute would be.
  private describeSaveError(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      switch (error.status) {
        case 0:
          return 'Could not reach the server. Check your connection and try again.';
        case 401:
        case 403:
          return 'You do not have permission to change this project.';
        case 404:
          return 'This project no longer exists. It may have been deleted.';
        case 422:
          return 'The server rejected this name. It may have just been taken by another project, or contain characters the server does not accept.';
        default:
          return `Could not save the project (server returned ${error.status}).`;
      }
    }

    return (error as Error)?.message || 'Failed to save project';
  }

  private navigateAfterSuccess(project: Project, wasUpdate: boolean): void {
    if (wasUpdate) {
      // Navigate to project detail after rename
      this.router.navigate(['/projects', project.id]);
    } else {
      // Navigate to project reports after create
      this.router.navigate(['/projects', project.id, 'reports']);
    }
  }
}
